import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { z } from 'zod';
import DOMPurify from 'dompurify';
import toast, { Toaster } from 'react-hot-toast';
import { useCreateLearner, useAllGroups, updateLearner } from '../../services/learners';
import { uploadFile } from '../../services/uploads';
import UploadImageLearner from '@/components/icons/UploadImageLearner';
import DepartmentSelect from '../../components/common/DepartmentSelect';
import RoleSelect from '../../components/common/RoleSelect';
import LocationSelect from '../../components/common/LocationSelect';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';

const schema = z.object({
  full_name: z
    .string()
    .min(2, 'Full name is required')
    .max(200, 'Name must be 200 characters or less')
    .refine(v => v.trim().includes(' '), 'Please enter both first and last name'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email address'),
  group_ids: z.array(z.string()).optional(),
  department_id: z.string().optional(),
  role_id: z.string().optional(),
  location_id: z.string().optional(),
});

type FormData = z.infer<typeof schema>;
type FieldErrors = Partial<Record<keyof FormData, string>>;

const sanitize = (v: string) => DOMPurify.sanitize(v, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

const inputBase =
  'w-full px-3.5 py-4 border rounded-lg bg-background-primary text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-200 transition-colors';
const inputBorder = 'border-[rgba(189,188,201,0.4)]';
const inputError = 'border-error-primary';


const LearnerCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { bulkTools } = usePlanFeatures();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const createMutation = useCreateLearner();
  const { data: groups } = useAllGroups();

  const [form, setForm] = useState<FormData>({ full_name: '', email: '', group_ids: [], department_id: undefined, role_id: undefined, location_id: undefined });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: sanitize(value) }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleNameBlur = () => {
    // Validate the name on blur so users get feedback without waiting for submit.
    // Skip if the field is still empty — don't nag before they've had a chance.
    if (!form.full_name.trim()) return;
    const result = schema.shape.full_name.safeParse(form.full_name);
    if (!result.success) {
      setErrors(prev => ({ ...prev, full_name: result.error.issues[0]?.message }));
    }
  };

  const handleGroupToggle = (groupId: string) => {
    setForm(prev => {
      const ids = prev.group_ids ?? [];
      return {
        ...prev,
        group_ids: ids.includes(groupId) ? ids.filter(id => id !== groupId) : [...ids, groupId],
      };
    });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only *.jpeg, *.jpg, *.png, *.gif files are allowed');
      return;
    }
    if (file.size > 3.1 * 1024 * 1024) {
      toast.error('File size must be under 3.1 MB');
      return;
    }
    setPhotoFile(file);
    setSelectedPlaceholder(null);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handlePlaceholderSelect = (path: string) => {
    setSelectedPlaceholder(path);
    setPhotoFile(null);
    setPhotoPreview(path);
  };

  const validate = (): boolean => {
    const result = schema.safeParse(form);
    if (result.success) { setErrors({}); return true; }
    const errs: FieldErrors = {};
    result.error.issues.forEach(issue => {
      const field = issue.path[0] as keyof FormData;
      errs[field] = issue.message;
    });
    setErrors(errs);
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const parts = form.full_name.trim().split(' ');
    const first_name = parts[0];
    const last_name = parts.slice(1).join(' ');

    try {
      // Step 1: Create the learner
      const learner = await createMutation.mutateAsync({
        first_name,
        last_name,
        email: form.email,
        group_ids: form.group_ids?.length ? form.group_ids : undefined,
        department_id: form.department_id,
        role_id: form.role_id,
        location_id: form.location_id,
      });

      // Step 2: Upload avatar if one was selected (custom file or placeholder)
      const fileToUpload: File | null = photoFile ?? await (async () => {
        if (!selectedPlaceholder) return null;
        const res = await fetch(selectedPlaceholder);
        const blob = await res.blob();
        const name = selectedPlaceholder.split('/').pop() ?? 'avatar.jpg';
        return new File([blob], name, { type: blob.type || 'image/jpeg' });
      })();

      if (fileToUpload) {
        setIsUploading(true);
        try {
          const upload = await uploadFile({
            file: fileToUpload,
            resource_type: 'learner_avatar',
            resource_id: learner.id,
          });
          // Step 3: Patch learner with the S3 key (not the expiring signed URL)
          await updateLearner(learner.id, { avatar_url: upload.key });
        } catch {
          // Avatar upload failed — learner still created, warn but don't block
          toast.error('Learner created, but avatar upload failed. You can update it later.');
          navigate(`/learners/${learner.id}`);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      toast.success('Learner created successfully');
      navigate(`/learners/${learner.id}`);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to create learner. Please try again.';
      toast.error(message);
    }
  };

  return (
    <motion.div {...slideUp} className="space-y-6">
      <Toaster position="top-right" />

      {/* Header */}
      <PageHeader title="Add a New Learner" trail={[{ label: 'Learners', to: '/learners' }]} />

      <form onSubmit={handleSubmit}>
        {/* Two-column grid on desktop, stacked on mobile */}
        <div
          className="flex flex-col gap-4 md:grid md:gap-6 md:items-start"
          style={{ gridTemplateColumns: '2fr 1fr' }}
        >
          {/* ── Left: main form card ── */}
          <div className="order-2 md:order-1 card p-6 space-y-4">
            {/* Full name + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  name="full_name"
                  value={form.full_name}
                  onChange={handleChange}
                  onBlur={handleNameBlur}
                  placeholder="Full name"
                  className={`${inputBase} ${errors.full_name ? inputError : inputBorder}`}
                />
                {errors.full_name && (
                  <p className="mt-1 text-xs text-error-700">{errors.full_name}</p>
                )}
              </div>
              <div>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email address"
                  className={`${inputBase} ${errors.email ? inputError : inputBorder}`}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-error-700">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Department + Role */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DepartmentSelect
                value={form.department_id ?? ''}
                onChange={(id) => setForm(prev => ({ ...prev, department_id: id || undefined }))}
              />
              <RoleSelect
                value={form.role_id ?? ''}
                onChange={(id) => setForm(prev => ({ ...prev, role_id: id || undefined }))}
              />
            </div>

            {/* Location */}
            <LocationSelect
              value={form.location_id ?? ''}
              onChange={(id) => setForm(prev => ({ ...prev, location_id: id || undefined }))}
            />

            {/* Groups */}
            {groups && groups.length > 0 && (
              <div>
                <p className="text-xs font-medium text-text-secondary mb-2">Assign to groups</p>
                <div className="flex flex-wrap gap-2">
                  {groups.map(g => {
                    const selected = form.group_ids?.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => handleGroupToggle(g.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${ selected ? 'bg-primary-600 dark:bg-primary-350 border-primary-600 dark:border-primary-350' : 'bg-background-primary text-text-secondary border-[rgba(189,188,201,0.4)] hover:border-primary-300' }`}
                      >
                        {g.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Right: photo upload + email verified (on top on mobile) ── */}
          <div className="order-1 md:order-2 space-y-4">
            {/* Photo upload card */}
            <div className="card p-6 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="w-48 h-48 rounded-full bg-background-secondary hover:border-primary-400 transition-colors flex flex-col items-center justify-center gap-1.5 group overflow-hidden flex-shrink-0"
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <UploadImageLearner />
                    <span className="text-xs text-text-secondary group-hover:text-primary-500 transition-colors">
                      Upload photo
                    </span>
                  </>
                )}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept=".jpeg,.jpg,.png,.gif"
                className="hidden"
                onChange={handlePhotoChange}
              />
              <p className="text-xs text-text-secondary text-center leading-relaxed">
                Allowed *.jpeg, *.jpg, *.png, *.gif
                <br />Max size of 3.1 MB
              </p>

              {/* Placeholder picker */}
              <div className="w-full pt-2 border-t border-[rgba(189,188,201,0.2)]">
                <p className="text-xs text-text-secondary text-center mb-2">Or choose a placeholder</p>
                <div className="flex items-center justify-center gap-2">
                  {[1, 2, 3, 4].map(n => {
                    const path = `/learners-placeholders/placeholder-${n}.jpg`;
                    const isSelected = selectedPlaceholder === path;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => handlePlaceholderSelect(path)}
                        className={`w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 transition-[border-color,transform,box-shadow] duration-150 ${
                          isSelected
                            ? 'border-primary-500 ring-2 ring-primary-200 scale-110'
                            : 'border-transparent hover:border-primary-300'
                        }`}
                      >
                        <img
                          src={path}
                          alt={`Placeholder ${n}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Email verified card */}
            <div className="card p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-primary">Email verified</p>
                  <p className="text-xs text-text-secondary mt-1 leading-relaxed">
                    Disabling this will automatically send the user a verification email
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEmailVerified(v => !v)}
                  aria-checked={emailVerified}
                  role="switch"
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 mt-0.5 ${
                    emailVerified ? 'bg-primary-600' : 'bg-grey-300 dark:bg-grey-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      emailVerified ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="relative z-50 flex items-center gap-3 mt-6 md:justify-end">
          <button
            type="button"
            onClick={() => navigate('/learners')}
            className="hidden md:inline-flex btn-outline px-6"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || isUploading}
            className="flex-1 md:flex-none md:w-[150px] btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? 'Uploading…' : createMutation.isPending ? 'Adding…' : 'Add Learner'}
          </button>
          {bulkTools && (
            <button
              type="button"
              onClick={() => navigate('/learners/bulk')}
              className="flex-1 md:flex-none md:w-[150px] px-4 py-2.5 rounded-full bg-grey-900 text-white text-sm font-medium hover:bg-grey-800 transition-colors"
            >
              Bulk Upload
            </button>
          )}
        </div>
      </form>
    </motion.div>
  );
};

export default LearnerCreatePage;
